import { FC } from 'react';
import { Button } from '@/components/ui/button';
import { LayoutGrid, Sandwich, Coffee, Plus, Cookie, IceCream2 as IceCream, Salad, Pizza, UtensilsCrossed, ChefHat, Package } from 'lucide-react';
import { ProductCard } from '../ProductCard';
import { ProductGridProps } from './ProductGrid.types';

// Mapeo de iconos por nombre de icono string
const iconMap = {
  LayoutGrid,
  Sandwich,
  Coffee,
  Plus,
  Cookie,
  IceCream,
  Salad,
  Pizza,
  UtensilsCrossed,
  ChefHat,
  Package
};

export const ProductGrid: FC<ProductGridProps> = ({
  products,
  categories,
  searchQuery,
  selectedCategory,
  loading = false,
  error = null,
  hideCategories = false,
  onProductSelect,
  onSearchChange,
  onCategoryChange
}) => {
  const handleCategoryClick = (categoryId: string) => {
    onCategoryChange(categoryId);
  };

  return (
    <div className="h-full flex flex-col" data-testid="product-grid">
      {/* Header con categorías - Only show if not hidden */}
      {!hideCategories && (
        <div className="px-4 py-2 border-b bg-white">
          {/* Filtros de categoría - Optimizado para touch */}
          <div className="flex gap-2 sm:gap-3 overflow-x-auto">
            {categories.map(category => {
              // Obtener el icono correspondiente
              const IconComponent = category.icon ? iconMap[category.icon as keyof typeof iconMap] : Package;

              return (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  className={`whitespace-nowrap flex items-center gap-1.5 sm:gap-2 h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-medium transition-all duration-200 min-w-[100px] sm:min-w-[120px] ${
                    selectedCategory === category.id
                      ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md'
                      : 'bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-rose-300'
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {IconComponent && <IconComponent className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                  <span className="font-medium">{category.label || category.name}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid de productos */}
      <div className="flex-1 p-2 sm:p-3 md:p-4 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando productos...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-500">
              <div className="text-4xl mb-4">⚠️</div>
              <p className="text-lg font-medium">Error al cargar productos</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 content-start">
            {products.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onSelectProduct={onProductSelect}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-lg font-medium">No se encontraron productos</p>
              <p className="text-sm">Intenta con otros términos de búsqueda</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};